// ============================================================================
// FILE: src/modules/ticket/hooks/usePerformanceDashboard.js
// PURPOSE:
//   State & data orchestration hook for Performance Dashboard.
//
// ENHANCEMENT (STEP C5):
//   - Adds comparison mode (current vs previous period)
//   - Frontend-only, non-breaking
// ============================================================================

import { useEffect, useState, useCallback, useContext, useMemo } from "react";
import { AuthContext } from "../../../context/AuthContext";
import {
  getTenantPerformance,
  getLocationPerformance,
  getExecutivePerformance,
} from "../services/performanceDashboardService";

const DEFAULT_TAB = "tenant";

// ------------------------------------------------------------------
// Helper: default last 30 days
// ------------------------------------------------------------------
function getDefaultDateRange() {
  const today = new Date();
  const toDate = today.toISOString().slice(0, 10);

  const fromObj = new Date();
  fromObj.setDate(today.getDate() - 30);
  const fromDate = fromObj.toISOString().slice(0, 10);

  return { fromDate, toDate };
}

// ------------------------------------------------------------------
// Helper: resolve period → date range (FINAL, COMPLETE)
// ------------------------------------------------------------------
function resolveDateRangeFromPeriod(period) {
  if (!period) return null;

  const today = new Date();
  const to = new Date(today);
  const from = new Date(today);

  switch (period) {
    case "last_7_days":
      from.setDate(today.getDate() - 7);
      break;

    case "last_30_days":
      from.setDate(today.getDate() - 30);
      break;

    case "this_month":
    case "month_to_date":
    case "mtd":               // ✅ FIX
      from.setDate(1);
      break;

    case "year_to_date":
    case "ytd":               // ✅ FIX
      from.setMonth(0, 1);    // Jan 1
      break;

    default:
      return null;
  }

  return {
    from_date: from.toISOString().slice(0, 10),
    to_date: to.toISOString().slice(0, 10),
  };
}

// ------------------------------------------------------------------
// Helper: previous period calculation (GUARDED)
// ------------------------------------------------------------------
function calculatePreviousPeriod(fromDate, toDate) {
  if (!fromDate || !toDate) return null;

  const start = new Date(fromDate);
  const end = new Date(toDate);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

  const durationMs = end.getTime() - start.getTime();
  const prevEnd = new Date(start.getTime() - 24 * 60 * 60 * 1000);
  const prevStart = new Date(prevEnd.getTime() - durationMs);

  return {
    from_date: prevStart.toISOString().slice(0, 10),
    to_date: prevEnd.toISOString().slice(0, 10),
  };
}

// ------------------------------------------------------------------
// Helper: safe comparison builder
// ------------------------------------------------------------------
function buildComparison(current, previous) {
  const curr = Number(current) || 0;
  const prev = Number(previous) || 0;

  const delta = curr - prev;

  let deltaPct = 0;
  if (prev === 0) {
    deltaPct = curr === 0 ? 0 : 100;
  } else {
    deltaPct = (delta / prev) * 100;
  }

  let trend = "flat";
  if (delta > 0) trend = "up";
  else if (delta < 0) trend = "down";

  return {
    value: curr,
    prevValue: prev,
    delta,
    deltaPct,
    trend,
  };
}

export default function usePerformanceDashboard() {
  const { activeScope } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB);

  const defaultDates = getDefaultDateRange();

  // ------------------------------------------------------------------
  // Unified Filters
  // ------------------------------------------------------------------
  const [filters, setFilters] = useState({
    from_date: defaultDates.fromDate,
    to_date: defaultDates.toDate,

    period: null,
    status: null,
    sla_status: null,
    assigned: null,

    location_ids: [],
    user_id: null,
  });

  const [data, setData] = useState({
    tenant: null,
    locations: [],
    executives: [],
  });

  const [previousData, setPreviousData] = useState({
    tenant: null,
    locations: [],
    executives: [],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ------------------------------------------------------------------
  // Build API params
  // ------------------------------------------------------------------
  const buildApiParams = (overrideFilters = {}) => {
    const params = {
      tenant_id: activeScope?.tenant_id,
      location_id: activeScope?.location_id,
    };

    const finalFilters = { ...filters, ...overrideFilters };

    if (
      finalFilters.period &&
      (!finalFilters.from_date || !finalFilters.to_date)
    ) {
      const resolved = resolveDateRangeFromPeriod(finalFilters.period);
      if (resolved) {
        finalFilters.from_date = resolved.from_date;
        finalFilters.to_date = resolved.to_date;
      }
    }

    Object.entries(finalFilters).forEach(([key, value]) => {
      if (
        value !== null &&
        value !== "" &&
        !(Array.isArray(value) && value.length === 0)
      ) {
        params[key] = value;
      }
    });

    return params;
  };

  // ------------------------------------------------------------------
  // Fetch Data
  // ------------------------------------------------------------------
  const fetchData = useCallback(async () => {
    if (!activeScope?.tenant_id || !activeScope?.location_id) return;

    setLoading(true);
    setError(null);

    try {
      const currentParams = buildApiParams();

      const previousPeriod = calculatePreviousPeriod(
        currentParams.from_date,
        currentParams.to_date
      );

      const previousParams = previousPeriod
        ? buildApiParams(previousPeriod)
        : null;

      let currentResponse;
      let previousResponse;

      if (activeTab === "tenant") {
        currentResponse = await getTenantPerformance(currentParams);
        if (previousParams)
          previousResponse = await getTenantPerformance(previousParams);
      } else if (activeTab === "location") {
        currentResponse = await getLocationPerformance(currentParams);
        if (previousParams)
          previousResponse = await getLocationPerformance(previousParams);
      } else {
        currentResponse = await getExecutivePerformance(currentParams);
        if (previousParams)
          previousResponse = await getExecutivePerformance(previousParams);
      }

      if (currentResponse?.success) {
        setData({
          tenant: currentResponse.data?.tenant ?? null,
          locations: currentResponse.data?.locations ?? [],
          executives: currentResponse.data?.executives ?? [],
        });
      }

      if (previousResponse?.success) {
        setPreviousData({
          tenant: previousResponse.data?.tenant ?? null,
          locations: previousResponse.data?.locations ?? [],
          executives: previousResponse.data?.executives ?? [],
        });
      }

      if (!currentResponse?.success) {
        setError(
          currentResponse?.message || "Failed to load performance data"
        );
      }
    } catch (err) {
      setError(err?.message || "Unexpected error occurred");
    } finally {
      setLoading(false);
    }
  }, [activeTab, filters, activeScope]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ------------------------------------------------------------------
  // Derived comparison data
  // ------------------------------------------------------------------
  const comparisonData = useMemo(() => {
    if (activeTab === "tenant" && data.tenant && previousData.tenant) {
      return {
        totalTickets: buildComparison(
          data.tenant.totalTickets,
          previousData.tenant.totalTickets
        ),
        slaPercentage: buildComparison(
          data.tenant.slaPercentage,
          previousData.tenant.slaPercentage
        ),
        mttr: buildComparison(data.tenant.mttr, previousData.tenant.mttr),
        mtta: buildComparison(data.tenant.mtta, previousData.tenant.mtta),
        escalations: buildComparison(
          data.tenant.escalations,
          previousData.tenant.escalations
        ),
        reopens: buildComparison(
          data.tenant.reopens,
          previousData.tenant.reopens
        ),
      };
    }

    return null;
  }, [activeTab, data, previousData]);

  // ------------------------------------------------------------------
  // Public API (PERIOD → DATE SYNC)
  // ------------------------------------------------------------------
  const updateFilters = (updates) => {
    if (updates.period) {
      const resolved = resolveDateRangeFromPeriod(updates.period);
      if (resolved) {
        updates = {
          ...updates,
          from_date: resolved.from_date,
          to_date: resolved.to_date,
        };
      }
    }

    setFilters((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const resetFilters = () => {
    setFilters({
      from_date: defaultDates.fromDate,
      to_date: defaultDates.toDate,
      period: null,
      status: null,
      sla_status: null,
      assigned: null,
      location_ids: [],
      user_id: null,
    });
  };

  return {
    activeTab,
    setActiveTab,

    filters,
    updateFilters,
    resetFilters,

    data,
    loading,
    error,
    refresh: fetchData,

    previousData,
    comparisonData,
  };
}
